import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { BaseFacade } from '@core/classes/base.facade';
import { StationLongDto } from '@api/rest';
import * as fromStation from './_ngrx/station.reducer';
import * as StationState from './_ngrx/station.selectors';
import { Injectable, computed, inject } from '@angular/core';
import { addFavorite, clearFavorites, removeFavorite } from './_ngrx/station.actions';
import { StationType } from '@app/shared/enums/station-type.enum';
import { map, take } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class StationFacade extends BaseFacade {
  private readonly state$: Store<fromStation.State>;
  private readonly selectedSubstations$: Observable<StationLongDto[]> = this.takeAll(StationState.getSelectedSubstations);
  private readonly selectedTourStations$: Observable<StationLongDto[]> = this.takeAll(StationState.getTourStations);

  readonly allStations$: Observable<StationLongDto[]> = this.takeAll(StationState.getStations);
  readonly selected$: Observable<StationLongDto> = this.takeAll(StationState.getSelected);
  readonly selectedId$ = this.takeAll(StationState.getSelectedId);
  readonly favorites$: Observable<string[]> = this.takeAll(StationState.getFavorites);

  public readonly selectedTourStationsSignal = toSignal(this.selectedTourStations$);
  public readonly selectedSubstationsSignal = toSignal(this.selectedSubstations$);
  public readonly allStationsSignal = toSignal(this.allStations$);
  public readonly selectedSignal = toSignal(this.selected$);
  public readonly favoritesSignal = toSignal(this.favorites$);
  public readonly favoriteStationsSignal = computed(() =>
    this.favoritesSignal()
      .map(favoriteId =>
        this.allStationsSignal()
        .find(station => station.id === favoriteId)
      )
  );
  public readonly selectedIdSignal = toSignal(this.selectedId$);

  public readonly randomNonFavoriteStations = computed(() => {
    return this.tippStationsSignal()
      .filter(station => !this.favoritesSignal().includes(station.id) && station.active === true)
      .sort(() => Math.random() - 0.5)
      .slice(0, 10);
  });


  public readonly tippStationsSignal = computed(() =>
    this.allStationsSignal()
      .filter(
        station => station.type === StationType.TIPP
      )
  );

  public readonly questionStationsSignal = computed(() =>
    this.allStationsSignal()
      .filter(
        station => station.type === StationType.QUESTION
      )
  );

  public readonly lexioconStationsSignal = computed(() =>
    this.allStationsSignal()
      .filter(
        station => station.type === StationType.LEXICON_ENTRY
      )
  );

  constructor() {
    const state$ = inject<Store<fromStation.State>>(Store);

    super(state$);

    this.state$ = state$;
  }

  addFavorite(favorite: string) {
    this.state$.dispatch(addFavorite({favorite}));
  }

  removeFavorite(favorite: string) {
    this.state$.dispatch(removeFavorite({favorite}));
  }

  clearFavorites() {
    this.state$.dispatch(clearFavorites());
  }

  getStationsByType(stationType: StationType): Observable<StationLongDto[]> {
    return this.allStations$
      .pipe(
        map((stations: StationLongDto[]) => stations.filter(station => station.type === stationType))
      );
  }

  getStationByType(stationType: StationType): Observable<StationLongDto> {
    return this.allStations$
      .pipe(
        take(1),
        map((stations: StationLongDto[]) => stations.find(station => station.type === stationType))
      );
  }

  getStationById(id: string): Observable<StationLongDto> {
    return this.allStations$
      .pipe(
        take(1),
        map((stations: StationLongDto[]) => stations.find(station => station.id === id))
      );
  }

  getStationByGermanTitle(germanTitle: string): Observable<StationLongDto> {
    return this.allStations$
      .pipe(
        take(1),
        map((stations: StationLongDto[]) => stations.find(station => station.title?.['de'] === germanTitle))
      );
  }


  public readonly getStationByIdSignal = (id: string) =>
  toSignal(this.getStationById(id), { initialValue: null });

  public toggleStationFromFavorites(stationId: string) {
    if (this.favoritesSignal().includes(stationId)) {
      this.removeFavorite(stationId);
    } else {
      this.addFavorite(stationId);
    }
  }
}
